import { useState, useEffect, type CSSProperties } from "react";
import { useArchiveNode } from "../../../lib/hooks/use-kuvio";
import { showToast } from "../primitives";
import type { LibraryNode } from "../../../lib/types";

interface ArchiveModalProps {
  open: boolean;
  onClose: () => void;
  /** Node(s) to archive. */
  nodes: LibraryNode[];
  onArchived?: () => void;
}

export function ArchiveModal({ open, onClose, nodes, onArchived }: ArchiveModalProps) {
  const archiveNode = useArchiveNode();
  // Default to cascading — archiving a container should hide its contents. The
  // user can uncheck to archive only the target node(s).
  const [cascade, setCascade] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setCascade(true);
      setBusy(false);
    }
  }, [open]);

  if (!open || nodes.length === 0) return null;

  const single = nodes.length === 1 ? nodes[0]! : null;
  const couldHaveChildren = nodes.some((n) => n.type === "folder" || n.type === "agent");

  async function handleConfirm() {
    setBusy(true);
    try {
      for (const n of nodes) {
        await archiveNode.mutateAsync({ id: n.id, cascade });
      }
      showToast(
        single ? `Archived '${single.name}'` : `Archived ${nodes.length} items`,
      );
      onArchived?.();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Archive failed";
      showToast(`Couldn't archive: ${msg}`, "error", true);
    } finally {
      setBusy(false);
    }
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };

  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 440,
    maxWidth: "90vw",
  };

  const title = single ? `Archive ${single.name}?` : `Archive ${nodes.length} items?`;

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div style={modal} role="dialog" aria-label="Archive">
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: "var(--color-text-primary)" }}>
          {title}
        </div>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
          Archiving hides {single ? "this node" : "these nodes"} from the workspace tree but
          preserves {single ? "it" : "them"} for recovery. Restore later with the{" "}
          <code style={{ fontSize: 12 }}>restore_node</code> MCP tool.
        </div>

        {couldHaveChildren && (
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: 13,
              color: "var(--color-text-primary)",
              cursor: "pointer",
              marginBottom: 16,
            }}
          >
            <input
              type="checkbox"
              checked={cascade}
              onChange={(e) => setCascade(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              Also archive everything inside.
              <span style={{ display: "block", fontSize: 12, color: "var(--color-text-tertiary)" }}>
                {cascade
                  ? "Descendants are archived too and restore together."
                  : "Only the selected node is archived; its contents stay intact but become unreachable until you restore the parent."}
              </span>
            </span>
          </label>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} disabled={busy} style={{ fontSize: 13, padding: "8px 14px" }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            style={{
              fontSize: 13,
              padding: "8px 14px",
              borderRadius: "var(--border-radius-md)",
              border: "none",
              fontWeight: 500,
              fontFamily: "var(--font-sans)",
              cursor: busy ? "default" : "pointer",
              background: "var(--c-red-600)",
              color: "#fff",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Archiving…" : single ? "Archive" : `Archive ${nodes.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
